import React, { Component } from 'react';
import { connect } from 'react-redux';
import LANGUAGES_CONTROLLER from '../../../controllers/languages';
import Logger, { LOG_LEVELS } from '../../../utils/logger';
import LanguageStringResolver from '../../fields/language/LanguageStringResolver';
import { resolveStatusKey, getProcessingStatusLabel } from '../../../utils/uploadedDocumentsHelper';
import { STooltip, SIcon, SRowExpander } from '@avalara/skylab-sdk/react';
import ProcessingStatusTag from '../shared/ProcessingStatusTag';
import InvoiceStatusTag from '../shared/InvoiceStatusTag';
import ActionColumnCell from '../shared/ActionColumnCell';

const log = new Logger('UPLOADED_DOCUMENTS_ITEM', LOG_LEVELS.DEBUG);

export class DocumentItem extends Component {
    expanderRef = React.createRef();

    handleRowClick = (e) => {
        const { canExpandDetailsRow, onDetailsRowExpanderClick } = this.props;
        if (!canExpandDetailsRow || e.target.closest('s-row-expander')) {
            return;
        }

        const expander = this.expanderRef.current;
        if (!expander) {
            return;
        }

        expander.expanded = !expander.expanded;
        onDetailsRowExpanderClick && onDetailsRowExpanderClick({ currentTarget: expander });
    };

    handleCheckboxChange = () => {
        const { onSelect, selected, hasChildRows, document } = this.props;

        if (onSelect) {
            if (hasChildRows) {
                onSelect(!selected, document.id, true);
            } else {
                onSelect(!selected, document.id, false);
            }
        }
    };

    renderHeaderRow() {
        const {
            selected,
            onSelect,
            hasSelectableEntries
        } = this.props;

        return (
            <tr data-testid="uploaded-doc-grid">
                <th className="expander-column"></th>
                <th className="filename-column"><LanguageStringResolver string="uploadedDocuments_fileName" /></th>
                <th className="date-column"><LanguageStringResolver string="uploadedDocuments_dateProcessed" /></th>
                <th><LanguageStringResolver string='uploadedDocuments_companyCode' /></th>
                <th><LanguageStringResolver string="uploadedDocuments_companyName" /></th>
                <th className="status-column"><LanguageStringResolver string="uploadedDocuments_fileStatus" /></th>
                <th className="status-column"><LanguageStringResolver string="uploadedDocuments_invoiceStatus" /></th>
                <th className="checkbox-column">
                    <div className="checkbox-wrapper">
                        {hasSelectableEntries ? (
                            <>
                                <input
                                    type="checkbox"
                                    className="unlabeled"
                                    checked={selected || false}
                                    onChange={() => onSelect && onSelect(!selected)}
                                    disabled={false}
                                    id="select-all-checkbox"
                                />
                                <STooltip key="select-all-checkbox-tooltip" triggerId="select-all-checkbox" tooltipId="select-all-checkbox-tooltip" placement="top">
                                    <span slot="content">
                                        <LanguageStringResolver string="uploadedDocuments_selectAllDocumentsOnPage" />
                                    </span>
                                </STooltip>
                            </>
                        ) : (
                            <>
                                <SIcon id="select-all-info" name="info-circle" style={{ cursor: 'pointer' }} />
                                <STooltip key="select-all-info-tooltip" triggerId="select-all-info" tooltipId="select-all-info-tooltip" placement="top">
                                    <span slot="content">
                                        <LanguageStringResolver string="uploadedDocuments_noSelectableDocumentsOnPage" />
                                    </span>
                                </STooltip>
                            </>
                        )}
                    </div>
                </th>
            </tr>
        );
    }

    formatProcessingDate(processingDate) {
        if (processingDate === null || processingDate === undefined) {
            return '-';
        }
        if (typeof processingDate !== 'string') {
            return LANGUAGES_CONTROLLER.format(this.props.languages, 'Unknown');
        }
        return processingDate.substring(0, 10) || '-';
    }

    renderFileName() {
        const { document, hasChildRows } = this.props;

        if (hasChildRows) {
            return <strong>{document.fileName}</strong>;
        }

        return document.fileName;
    }

    renderExpanderCell() {
        const { document, hasChildRows, canExpandDetailsRow, onDetailsRowExpanderClick, detailsRowId } = this.props;

        if (hasChildRows === undefined) {
            return null;
        }

        return (
            <td className="expander-cell">
                {canExpandDetailsRow && (
                    <SRowExpander
                        ref={this.expanderRef}
                        rowId={detailsRowId || `document-details-row-${document.id}`}
                        onClick={onDetailsRowExpanderClick}
                    />
                )}
            </td>
        );
    }

    renderDocumentRow() {
        const { document, canExpandDetailsRow, hasChildRows, selected, languages } = this.props;

        const statusKey = resolveStatusKey(document.processingStatus);
        const statusLabel = getProcessingStatusLabel(languages, statusKey);

        return (
            <tr
                className={canExpandDetailsRow ? 'expandable-row' : ''}
                data-testid="uploaded-doc-grid"
                onClick={canExpandDetailsRow ? this.handleRowClick : undefined}
                style={canExpandDetailsRow ? { cursor: 'pointer' } : undefined}
            >
                {this.renderExpanderCell()}

                <td className="filename-column">
                    <span className="filename-text">
                        {this.renderFileName()}
                    </span>
                </td>
                <td className="date-column">
                    {this.formatProcessingDate(document.processingDate)}
                </td>
                <td>{document.userCode || '-'}</td>
                <td className="name-column">{document.userName || '-'}</td>
                <td className="status-column">
                    <ProcessingStatusTag document={document} languages={languages} />
                </td>
                <td className="status-column">
                    <InvoiceStatusTag document={document} showCounts languages={languages} />
                </td>

                <ActionColumnCell
                    document={document}
                    statusKey={statusKey}
                    statusLabel={statusLabel}
                    selected={selected}
                    onChange={this.handleCheckboxChange}
                    hasChildRows={hasChildRows}
                    languages={languages}
                />
            </tr>
        );
    }

    render() {
        const { header, document } = this.props;

        if (header) {
            return this.renderHeaderRow();
        }

        if (!document) {
            return null;
        }

        return this.renderDocumentRow();
    }
}


function mapStateToProps(state, props) {
    log.debug('mapStateToProps: ', state);
    const defaultCompanyList = (state.companies && state.companies.data) || [];
    return {
        languages: state.languages,
        companyList: props.companyList || defaultCompanyList,
        featureFlags: state.featureFlag && state.featureFlag.data
    };
}


DocumentItem.defaultProps = {
    header: false,
    formInitialising: false,
    hasSelectableEntries: true
};

export default connect(mapStateToProps)(DocumentItem);
